import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldAlert, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function ExportViolations({ customerId }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const run = async () => {
    try {
      const res = await api.get("/violations/export", { params: { customer_id: customerId || undefined, start: start || undefined, end: end || undefined }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "violations.csv";
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("Violations CSV downloaded"); setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="flex items-center gap-2">
      {open && (
        <div className="flex items-center gap-1 text-xs" data-testid="violations-export-range">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 w-36" data-testid="violations-export-start" />
          <span className="text-slate-400">to</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 w-36" data-testid="violations-export-end" />
          <Button size="sm" className="h-8 bg-blue-700 hover:bg-blue-800" onClick={run} data-testid="violations-export-download">Download</Button>
        </div>
      )}
      <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen((o) => !o)} data-testid="violations-export-button"><Download className="h-3.5 w-3.5 mr-1" />{open ? "Close" : "Export CSV"}</Button>
    </div>
  );
}

export function ViolationsWidget({ customerId = null, showCompany = true, days = 90 }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/violations/overview", { params: { customer_id: customerId || undefined, days } })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((err) => toast.error(formatApiError(err)));
    return () => { cancelled = true; };
  }, [customerId, days]);
  if (!data) return null;
  const driversLink = customerId ? `/drivers?customer=${customerId}` : "/drivers";
  return (
    <Card className="overflow-hidden border-t-4 border-t-rose-600" data-testid="violations-widget">
      <div className="px-5 py-3 border-b border-rose-100 bg-rose-50/60 flex items-center justify-between">
        <div>
          <div className="tag-mono text-rose-700">VIOLATIONS</div>
          <h2 className="text-lg font-bold font-heading text-slate-900">Flagged drivers & recent violations</h2>
        </div>
        <div className="flex items-center gap-3"><ExportViolations customerId={customerId} /><ShieldAlert className="h-5 w-5 text-rose-600" /></div>
      </div>
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
        <div className="p-4">
          <div className="tag-mono text-slate-500 mb-2">FLAGGED · ≥{data.threshold} in 12 months or any crash</div>
          {data.flagged.length === 0 ? <div className="text-sm text-slate-500 py-3" data-testid="violations-flagged-empty">No flagged drivers.</div> : (
            <ul className="space-y-2" data-testid="violations-flagged-list">
              {data.flagged.slice(0, 8).map((f) => (
                <li key={f.driver_id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0"><Link to={driversLink} className="font-medium text-slate-900 hover:text-blue-700">{f.name}</Link>{showCompany && <span className="text-slate-400"> · {f.company}</span>}<div className="text-xs text-slate-500 truncate">{f.latest}</div></div>
                  <div className="flex gap-1 shrink-0">{f.crashes_12mo > 0 && <Badge className="bg-rose-600 text-white border-rose-700">{f.crashes_12mo} crash</Badge>}<Badge className="bg-rose-50 text-rose-700 border border-rose-200">{f.last_12mo} · {f.points_12mo} pts</Badge></div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4">
          <div className="tag-mono text-slate-500 mb-2">RECENT · last {data.days} days</div>
          {data.recent.length === 0 ? <div className="text-sm text-slate-500 py-3" data-testid="violations-recent-empty">No violations recorded recently.</div> : (
            <ul className="space-y-1.5" data-testid="violations-recent-list">
              {data.recent.slice(0, 8).map((v) => (
                <li key={v.id} className="text-sm flex items-start justify-between gap-2">
                  <div className="min-w-0"><span className="font-medium">{v.name}</span>{showCompany && <span className="text-slate-400"> · {v.company}</span>}<div className="text-xs text-slate-600 truncate">{v.description}</div></div>
                  <div className="text-right shrink-0"><div className="font-mono text-xs text-slate-500">{v.date || "—"}</div>{v.is_crash && <Badge className="bg-rose-600 text-white border-rose-700">CRASH</Badge>}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
